import api from './api';
import type { SavedCustomFilter } from './quote.service';
import type { FieldDefinition } from '../contexts/FieldDefinitionsContext';

/** The two landing-page views over the template set. */
export type TemplateView = 'template' | 'lineitem';

/** The two mutually-exclusive categories the board shows, both `deleted='N'`:
 *  'approved' is live + approved (the working set, ~2k), 'unapproved' is live and
 *  never approved (~12k drafts). DELETED templates are in neither — they are
 *  reachable only through a Custom Search or Custom Filter, which drop the status
 *  condition server-side. */
export type TemplateStatus = 'approved' | 'unapproved';

/** A template search result row. The column set differs per view, so keys are
 *  dynamic (see backend templates.py `_template_sql` / `_lineitem_sql`). */
export type TemplateRow = Record<string, string | number | null>;

export interface TemplateSearchParams {
  view: TemplateView;
  /** Orders-scope registry fieldnames to attach as extra columns, resolved
   *  against the TEMPLATE tables. Same registry the order grids pick from. */
  additionalFields?: string[];
  /** Which category to show. Ignored by the server when a customSearch or
   *  customFilter is active — those name what they want and span every status. */
  status: TemplateStatus;
  /** One-field multi-value blob `field~~=~~v1,v2~~|||` (pronumber/masterpro/
   *  billto_name/sage_tag/mas_tag/deliver_to/quotenumber — the allowlist lives in
   *  backend templates.py `_SEARCH_COLUMNS`, which also sets each field's match
   *  mode: prefix LIKE for deliver_to, bare-or-"T" for pronumber/masterpro, and
   *  numeric-only values for quotenumber). */
  customSearch?: string;
  /** Widen a customFilter to include DELETED templates (its Include Deleted toggle).
   *  Inert without a filter, and inert for a customSearch — a search names specific
   *  records and always spans every status. */
  includeDeleted?: boolean;
  /** JSON `FilterCondition[]` string from the Template Custom Filter dialog. The
   *  backend translates it against TEMPLATE_FILTER_REGISTRY and ANDs it with any
   *  customSearch — the two are independent, so either, both, or neither can be
   *  active. Either one makes the result span all statuses, deleted included. */
  customFilter?: string;
}

export interface TemplateSearchResult {
  view: TemplateView;
  /** True when the server hit the RESULT_CAP — the UI shows a "refine" banner. */
  capped: boolean;
  /** True when DELETED templates can actually be in these rows: a custom search
   *  (always status-blind) or a filter the user widened with Include Deleted. */
  includesDeleted: boolean;
  /** True when the chosen category applied to this result. False for any search or
   *  filter — a filter WITHOUT Include Deleted still spans both approval states, so
   *  this is deliberately not the inverse of `includesDeleted`. Both are
   *  server-derived so the UI's claims can't drift from the SQL that ran. */
  statusApplied: boolean;
  rows: TemplateRow[];
}

/** Session cache for the filter-field registry — it is static per deploy, so the
 *  dialog should fetch it once, not on every open. Follows the
 *  `cacheUntilForbidden` idiom in orderLookups.service.ts: a 403 (missing
 *  endpoint grant) can only be fixed by an admin, so it resolves EMPTY and stays
 *  cached rather than re-denying on every open and flooding the audit log; any
 *  other failure clears the cache so the next open retries. */
let filterFieldsCache: Promise<FieldDefinition[]> | null = null;

/** Test-only: drop the session cache so each case starts cold (precedent:
 *  __resetTemplatesSearchCache in TemplatesPage.tsx). */
export function __resetFilterFieldsCache(): void { filterFieldsCache = null; }

const templateService = {
  /** GET /api/v1/templates/search — the Templates landing page's only data call.
   *  Returns the full (capped) matching set; the grid sorts / filters / searches /
   *  exports client-side over it (mirrors the Quotes Accounts view). */
  async searchTemplates(params: TemplateSearchParams): Promise<TemplateSearchResult> {
    const qp: Record<string, string | boolean> = { view: params.view, status: params.status };
    if (params.customSearch) qp.custom_search = params.customSearch;
    if (params.customFilter) qp.custom_filter = params.customFilter;
    if (params.includeDeleted) qp.include_deleted = true;
    // Unsorted: the backend keeps the user's selection order.
    if (params.additionalFields?.length) qp.additional_fields = params.additionalFields.join(',');
    const response = await api.get('/templates/search', { params: qp });
    const data = response.data as Omit<TemplateSearchResult, 'includesDeleted' | 'statusApplied'>
      & { includes_deleted?: boolean; status_applied?: boolean };
    return {
      view: data.view,
      capped: data.capped,
      rows: data.rows,
      includesDeleted: !!data.includes_deleted,
      statusApplied: !!data.status_applied,
    };
  },

  /** POST /api/v1/templates — create a template from the drawer's full state, in
   *  ONE atomic request (#33471). Nothing is persisted before this fires: the
   *  drawer sits on the 'TNEW' sentinel until the user presses Save, so backing
   *  out leaves no row behind (V1 lib_editrecord.pl:10535-10570).
   *
   *  Same payload shape as ordersService.createOrder — the modal's three fields
   *  plus the staged form — because both go through the same create flow. */
  async createTemplate(payload: {
    movetype: string;
    billto: number;
    customercode: number;
    originateleg?: number;
    deliveryleg?: number;
    terminateleg?: number;
    legs?: unknown[];
    orderData?: unknown[];
    invoiceData?: unknown[];
    invliData?: unknown[];
    notes?: string[];
    submitNonce?: string;
    bypassDupeCheck?: boolean;
  }): Promise<{ pronumber: string; userMessages?: Array<{ text: string; level: string }> }> {
    const response = await api.post('/templates', payload);
    return response.data as {
      pronumber: string;
      userMessages?: Array<{ text: string; level: string }>;
    };
  },

  /** GET /api/v1/templates/filter-fields — the server-owned filter field registry
   *  (FieldDefinition-shaped; deriving it server-side means the picker and the SQL
   *  translator can never drift). Session-cached. */
  async getFilterFields(): Promise<FieldDefinition[]> {
    if (!filterFieldsCache) {
      filterFieldsCache = api
        .get<FieldDefinition[]>('/templates/filter-fields')
        .then((r) => r.data)
        .catch((err: any) => {
          if (err?.response?.status === 403) return [] as FieldDefinition[];
          filterFieldsCache = null;
          throw err;
        });
    }
    return filterFieldsCache;
  },

  /** GET /api/v1/templates/custom-filters — the current user's saved TEMPLATE
   *  custom filters (quote_custom_filters rows with scope='templates'). */
  async listCustomFilters(): Promise<SavedCustomFilter[]> {
    const response = await api.get('/templates/custom-filters');
    return response.data as SavedCustomFilter[];
  },

  /** POST /api/v1/templates/custom-filters — upsert a named template custom
   *  filter (per user, by name). */
  async saveCustomFilter(name: string, filter: string): Promise<{ recno: number }> {
    const response = await api.post('/templates/custom-filters', { name, filter });
    return response.data as { recno: number };
  },
};

export default templateService;
